import React from 'react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine } from 'recharts';

/**
 * TrajectoryChart - 2D ball flight visualization with comparison support
 *
 * Props:
 *   trajectoryData: Array of {x, y} coordinates in yards (active/selected)
 *   baselineData: Optional baseline trajectory for comparison
 *   carry: Carry distance in yards
 *   maxHeight: Maximum height in yards
 *   baselineCarry: Baseline carry for comparison
 *   baselineLabel: Label for baseline (e.g., "Current Driver")
 *   activeLabel: Label for active trajectory (e.g., "Qi10 Max + Ventus Blue")
 */
export function TrajectoryChart({
  trajectoryData,
  baselineData,
  carry,
  maxHeight,
  baselineCarry,
  baselineLabel = "Current Club",
  activeLabel = "Selected Build"
}) {
  if (!trajectoryData || trajectoryData.length === 0) {
    return (
      <div className="h-48 bg-page rounded-lg flex items-center justify-center text-zinc-500">
        No trajectory data available
      </div>
    );
  }

  // Calculate domain bounds including baseline if present
  const allX = [...trajectoryData.map(p => p.x)];
  const allY = [...trajectoryData.map(p => p.y)];
  if (baselineData && baselineData.length > 0) {
    allX.push(...baselineData.map(p => p.x));
    allY.push(...baselineData.map(p => p.y));
  }
  const maxX = Math.max(...allX, carry || 0, baselineCarry || 0) + 10;
  const maxY = Math.max(...allY, maxHeight || 0) + 5;

  // Calculate carry gain
  const carryGain = baselineCarry ? carry - baselineCarry : null;

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-card-solid border border-highlight rounded px-3 py-2 text-xs">
          <div className="text-zinc-400">Distance: <span className="text-white font-mono">{data.x} yds</span></div>
          <div className="text-zinc-400">Height: <span className="text-white font-mono">{data.y?.toFixed(1)} yds</span></div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-page rounded-lg p-4 border border-subtle">
      {/* Legend Bar */}
      <div className="flex flex-wrap justify-between items-center mb-3 gap-2">
        <div className="flex flex-wrap gap-4 text-xs">
          {baselineData && baselineData.length > 0 && (
            <div className="flex items-center gap-2">
              <div className="w-6 h-0 border-t-2 border-dashed border-zinc-500"></div>
              <span className="text-zinc-500">{baselineLabel}</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <div className="w-6 h-0.5 bg-blue-500 rounded"></div>
            <span className="text-blue-400">{activeLabel}</span>
          </div>
        </div>
        <div className="flex gap-4 text-xs">
          <span className="text-zinc-400">Carry: <span className="text-primary font-mono">{Math.round(carry)} yds</span></span>
          {carryGain !== null && carryGain !== 0 && (
            <span className={`font-mono font-bold ${carryGain > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {carryGain > 0 ? '+' : ''}{Math.round(carryGain)} yds
            </span>
          )}
        </div>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <LineChart margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />

          <XAxis
            dataKey="x"
            type="number"
            domain={[0, maxX]}
            tick={{ fill: '#71717a', fontSize: 10 }}
            axisLine={{ stroke: '#3f3f46' }}
            tickLine={{ stroke: '#3f3f46' }}
            label={{ value: 'Distance (yds)', position: 'bottom', fill: '#71717a', fontSize: 10, dy: 10 }}
          />

          <YAxis
            dataKey="y"
            type="number"
            domain={[0, maxY]}
            tick={{ fill: '#71717a', fontSize: 10 }}
            axisLine={{ stroke: '#3f3f46' }}
            tickLine={{ stroke: '#3f3f46' }}
            label={{ value: 'Height (yds)', angle: -90, position: 'insideLeft', fill: '#71717a', fontSize: 10 }}
          />

          <Tooltip content={<CustomTooltip />} />

          {/* Ground reference line */}
          <ReferenceLine y={0} stroke="#3f3f46" />

          {/* Baseline trajectory (Gray Dashed) */}
          {baselineData && baselineData.length > 0 && (
            <Line
              data={baselineData}
              type="monotone"
              dataKey="y"
              stroke="#71717a"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              isAnimationActive={false}
              name="baseline"
            />
          )}

          {/* Active trajectory (Blue Solid) */}
          <Line
            data={trajectoryData}
            type="monotone"
            dataKey="y"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            isAnimationActive={true}
            animationDuration={800}
            name="active"
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

export default TrajectoryChart;
